import React from 'react';
import { useSelector } from 'react-redux';
import { InputField } from 'components/form';
import { UserProfile } from '@trnsact/trnsact-shared-types';
import { ListingForm } from 'components/shared/TableListing';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { DeleteOutline, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip, Typography, Autocomplete, Chip, TextField } from '@mui/material';

import { Recipient, RecipientRole, StagesFormValues } from '../../../../types';
import { grey } from '@mui/material/colors';

interface Props {
  isEdit: boolean;
  rowIndex: number;
  onRemove: VoidFunction;
  onMoveRow: (from: number, to: number) => void;
}

export const TitleCell = ({ rowIndex, onRemove, onMoveRow, isEdit }: Props) => {
  const { control } = useFormContext<ListingForm<StagesFormValues>>();
  const rows = useWatch({ control, name: 'bodyFields' });

  const users = useSelector<any, UserProfile[]>(state => state.relatedUsers)
    .filter(({ status, fullName }) => status !== 'blocked' && !!fullName)
    .map(({ id, fullName }) => ({ userProfileId: id, name: fullName!, role: undefined }));

  const RECIPIENT_OPTIONS: Recipient[] = [
    { role: RecipientRole.SALES_MANAGER, name: 'Sales Manager', userProfileId: undefined },
    { role: RecipientRole.SALES_REP, name: 'Sales Rep', userProfileId: undefined },
    { role: RecipientRole.FINANCE_MANAGER, name: 'Finance Manager', userProfileId: undefined },
    ...users,
  ];

  return (
    <Stack direction="row" gap="1rem" width="100%" alignItems="flex-start">
      <Box sx={{ alignSelf: 'center', borderRadius: '100%', padding: '0.25rem 0.75rem', backgroundColor: grey[200] }}>
        <Typography variant="caption">{rowIndex + 1}</Typography>
      </Box>

      <Stack width="100%" gap="1rem">
        <InputField
          control={control}
          name={`bodyFields.${rowIndex}.name`}
          inputProps={{ variant: 'outlined', placeholder: 'Enter stage name', required: true }}
        />

        <Controller
          control={control}
          name={`bodyFields.${rowIndex}.recipients`}
          render={({ field }) => (
            <Autocomplete
              multiple
              fullWidth
              disableCloseOnSelect
              filterSelectedOptions
              value={field.value ?? []}
              options={RECIPIENT_OPTIONS}
              onChange={(_, next) => field.onChange(next)}
              getOptionLabel={o => o.name ?? ''}
              isOptionEqualToValue={(opt, val) =>
                `${opt.userProfileId ?? ''}-${opt.role ?? ''}` === `${val.userProfileId ?? ''}-${val.role ?? ''}`
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={`${option.userProfileId ?? ''}-${option.role ?? ''}-${option.name}`}
                    label={option.name}
                    size="small"
                  />
                ))
              }
              renderOption={(props, option) => (
                <li {...props} key={`${option.userProfileId ?? ''}-${option.role ?? ''}-${option.name}`}>
                  <Typography variant="body2">{option.name}</Typography>
                </li>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  label="Notification Recipients"
                  placeholder={(field.value?.length ?? 0) ? '' : 'Select recipients'}
                  helperText="Selected recipients will be notified when the stage is completed"
                />
              )}
            />
          )}
        />
      </Stack>

      {!isEdit && (
        <Stack direction="row" alignItems="center">
          <Tooltip title="Remove configuration">
            <IconButton size="small" color="error" onClick={onRemove}>
              <DeleteOutline />
            </IconButton>
          </Tooltip>

          <Tooltip title="Move row down">
            <IconButton
              size="small"
              disabled={rowIndex + 1 > rows.length - 1}
              onClick={() => onMoveRow(rowIndex, rowIndex + 1)}
            >
              <KeyboardArrowDown />
            </IconButton>
          </Tooltip>

          <Tooltip title="Move row up">
            <IconButton size="small" disabled={!rowIndex} onClick={() => onMoveRow(rowIndex, rowIndex - 1)}>
              <KeyboardArrowUp />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );
};
